import { HttpClient } from '@angular/common/http';
import { inject, Injectable, Injector, runInInjectionContext } from '@angular/core';
import { AuthUtils } from 'app/core/auth/auth.utils';
import { UserService } from 'app/core/user/user.service';
import { catchError, from, map, Observable, of, Subject, switchMap, tap, throwError } from 'rxjs';
import { ApiAuthService } from '../apis/api-auth.service';
import { AuthCredential } from '../models/auth-data.model';
import { DataStoreService } from '../datas/data-store.service';
import { Auth, signInWithCustomToken, signOut as firebaseSignOut } from '@angular/fire/auth';
import { UserDataService } from '../datas/user-data';
import { TranslocoService } from '@ngneat/transloco';

/**
 * Permission level of each basic role, mirroring `AppScope.permission_level`
 * on the API. Higher wins; a user's level is the highest of their roles.
 */
export const AppRoleLevels: { [role: string]: number } = {
    site: 60,
    project_site: 70,
    company: 75,
    project_owner: 80,
    project_operator: 85,
    app_owner: 90
};

@Injectable({ providedIn: 'root' })
export class AuthService {
    private _authenticated: boolean = false;
    private _httpClient = inject(HttpClient);
    private _apiAuthSvc = inject(ApiAuthService);
    private _dataStoreSvc = inject(DataStoreService);
    private _userDataSvc = inject(UserDataService);
    private auth: Auth = inject(Auth);
    private _injector = inject(Injector);
    private _translocoSvc = inject(TranslocoService);

    private readonly KeyAuthCredential = 'auth:credential';
    private authCredential: AuthCredential;

    /**
     * Emits whenever the stored credential changes.
     * SessionExpiryService listens on this to (re)schedule its watchdog without
     * AuthService having to depend on it (which would be a circular injection).
     */
    private _authCredentialChanged: Subject<AuthCredential | null> = new Subject<AuthCredential | null>();

    get authCredentialChanged$(): Observable<AuthCredential | null> {
        return this._authCredentialChanged.asObservable();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Setter & getter for access token
     */
    set accessToken(token: string) {
    }

    get accessToken(): string {
        const authCred = this.getAuthCredential();
        if (authCred) {
            return this.authCredential.accessToken;
        }
        return null;
    }

    setAuthCredential(cred: AuthCredential) {
        this.authCredential = cred;
        this._dataStoreSvc.setObject(this.KeyAuthCredential, cred.rawJson);
        this._authCredentialChanged.next(cred);
    }

    getAuthCredential(): AuthCredential {

        if (!this.authCredential) {
            const json = this._dataStoreSvc.getObject(this.KeyAuthCredential);
            if (json) {
                this.authCredential = AuthCredential.parse(json);
            }
        }

        return this.authCredential;
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------
    isTokenExpired() {
        const authCred = this.getAuthCredential();
        if (!authCred) {
            return true;
        }
        return authCred.isExpire();
    }

    /**
     * Absolute expiry of the current access token, as epoch seconds.
     * Returns 0 when there is no credential.
     */
    getTokenExpiresAt(): number {
        return this.getAuthCredential()?.tokenExpires ?? 0;
    }

    /**
     * Exchange the stored refresh token for a fresh access token.
     * The response carries `expires_in` but no `expire_ts`, so AuthCredential.parse()
     * computes a new absolute expiry for us.
     */
    refreshAuthToken(): Observable<AuthCredential> {
        const authCred = this.getAuthCredential();

        if (!authCred || !authCred.refreshToken) {
            return throwError(() => new Error('No refresh token available'));
        }

        const payload = {
            refresh_token: authCred.refreshToken
        }

        return this._apiAuthSvc.apiPostRefreshToken(payload, {}).pipe(
            tap((auth: AuthCredential) => this.assertHasRole(auth)),
            tap((auth: AuthCredential) => {
                this.setAuthCredential(auth);
                this._authenticated = true;
            }),
        );
    }

    hasBasicRole(role: string) {
        let valid: boolean = false;
        let authCred: AuthCredential = this.getAuthCredential();
        if (authCred) {
            let index = authCred.basicRole.findIndex(x => x === role);
            if (index > -1) {
                valid = true;
            }
        }
        return valid;
    }

    /**
     * A token without any basic role belongs to a user who was removed from every team
     * (the user record itself is kept). Reject it before anything is stored.
     */
    private assertHasRole(auth: AuthCredential): void {
        if (!auth.basicRole?.length) {
            const message = this._translocoSvc.translate('mods.auth.noAccess');
            throw { code: 'NO_ROLE', description: message, message };
        }
    }

    /**
     * Permission level of the current user in the app hierarchy.
     *
     * The API sends the highest `AppScope.permission_level` of the user's
     * company roles as `level`. Older stored credentials may predate that
     * field, so the basic roles are used as a fallback ceiling.
     */
    getLevel(): number {
        const authCred: AuthCredential = this.getAuthCredential();
        if (!authCred) {
            return 0;
        }

        let level = authCred.level ?? 0;
        (authCred.basicRole ?? []).forEach((role) => {
            const roleLevel = AppRoleLevels[role] ?? 0;
            if (roleLevel > level) {
                level = roleLevel;
            }
        });

        return level;
    }

    /**
     * Forgot password
     *
     * @param email
     */
    forgotPassword(email: string): Observable<any> {
        const payload = {
            email: email
        }

        return this._apiAuthSvc.apiPostEmailResetPasswordCode(payload, {});
    }

    forgotPasswordVerifyCode(email: string, code: string): Observable<any> {
        const payload = {
            email: email,
            code: code
        }

        return this._apiAuthSvc.apiPostEmailResetPasswordCodeVerification(payload, {});
    }

    /**
     * Reset password
     *
     * @param password
     */
    resetPassword(email: string, code: string, password: string): Observable<any> {
        const payload = {
            email: email,
            new_password: password,
            code: code,
            captcha: ''
        }
        return this._apiAuthSvc.apiPostEmailResetPassword(payload, {});
    }


    /**
     * Sign in via Email
     *
     */
    signInUsingEmail(email: string, password: string, captcha?: { token: string; version: 'v3' | 'v2' }): Observable<any> {
        if (this._authenticated) {
            return throwError(() => 'User is already logged in.');
        }

        const payload = {
            username: email,
            username_type: 'email',
            password: password,
            // reCAPTCHA (see RecaptchaService); `captcha` kept for older backends
            captcha: captcha?.token ?? '',
            captcha_token: captcha?.token ?? '',
            captcha_version: captcha?.version ?? '',
            hw_id: '',
        }

        return this._apiAuthSvc.apiPostEmailLogin(payload, {}).pipe(
            tap((auth: AuthCredential) => this.assertHasRole(auth)),
            switchMap((auth: AuthCredential) => {
                // Store the access token in the local storage
                this.authCredential = auth;
                this.setAuthCredential(auth);

                this.accessToken = auth.accessToken;
                // Set the authenticated flag to true
                this._authenticated = true;

                // Return a new observable with the response
                return of(auth);
            }),
        );
    }

    /**
     * Sign in using the Firebase custom token
     */
    signInUsingFirebaseCustomToken(payload: any, params: any) {
        this._apiAuthSvc.apiPostFirebaseCustomToken(payload, params).subscribe({
            next: res => {
                let customToken = res['custom_token'];
                // Firebase APIs must be called inside an injection context (we are in an HTTP callback here)
                runInInjectionContext(this._injector, () => signInWithCustomToken(this.auth, customToken))
                    .then((userCredential) => {
                    })
                    .catch((error) => {
                        const errorCode = error.code;
                        const errorMessage = error.message;

                        console.error('signInWithCustomToken errorCode:', errorCode);
                        console.error('signInWithCustomToken errorMessage:', errorMessage);

                    });
            },
            complete: () => {

            },
            error: err => {
                console.error('firebaseCustomLogin error:', err);
            }
        })
    }

    /**
     * Sign out
     */
    signOut(): Observable<any> {
        // Remove the access token from the local storage
        if (this.accessToken) {
            const logoutPayload = {
                'token': `Bearer ${this.accessToken}`
            }

            this._apiAuthSvc.apiPostLogout(logoutPayload, {}).subscribe({
                next: res => {

                },
                complete: () => {

                },
                error: err => {
                    console.error('logout error:', err);
                }
            });
        }

        this._dataStoreSvc.remove(this.KeyAuthCredential);
        // End the Firebase session too: the SDK keeps it in IndexedDB across logins
        const firebaseSignedOut = runInInjectionContext(this._injector, () => firebaseSignOut(this.auth))
            .catch(err => console.error('firebase signOut error:', err));
        // Set the authenticated flag to false
        this._authenticated = false;
        this.authCredential = null;
        this._userDataSvc.clearStorage();
        this._authCredentialChanged.next(null);
        // Emits once the Firebase session is gone too, so callers can safely redirect
        return from(firebaseSignedOut).pipe(map(() => true));
    }

    /**
     * Go to the sign-in page with a full page load. Reloading drops every in-memory
     * cache (teams, permissions, vocabularies, ...), so the next account to sign in on
     * this browser never sees the previous account's data.
     */
    redirectToSignIn(): void {
        window.location.assign('/auth/sign-in');
    }

    /**
     * Sign up
     *
     * @param user
     */
    signUp(user: { name: string; email: string; password: string; company: string }): Observable<any> {
        return this._httpClient.post('api/auth/sign-up', user);
    }

    /**
     * Unlock session
     *
     * @param credentials
     */
    unlockSession(credentials: { email: string; password: string }): Observable<any> {
        return this._httpClient.post('api/auth/unlock-session', credentials);
    }

    /**
     * Check the authentication status
     */
    check(): Observable<boolean> {

        // Check if the user is logged in
        if (this._authenticated) {
            return of(true);
        }

        // Check the access token availability
        if (!this.accessToken) {
            return of(false);
        }

        // Check the access token expire date
        if (this.isTokenExpired()) {
            return of(false);
        }

        this.accessToken = this.authCredential.accessToken
        this._authenticated = true;

        // If the access token exists, and it didn't expire, sign in using it
        return of(true);
    }
}
